import { useCallback, useRef, useState } from 'react';
import { normalizeImageFile } from '../utils/heic';

interface Props {
  /** Bold main text inside the dropzone — e.g. "Upload Photo", "Upload Your Photo". */
  title: string;
  /** Secondary lines under the title — e.g. ["Subject 1", "Full body or clear face shot"]. */
  lines?: string[];
  file: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  error?: string | null;
}

// HEIC/HEIF (iPhone's default camera format) is accepted here too — it's
// converted to JPEG in-browser (see utils/heic.ts) before the regex check
// below runs. Without listing it, some OS file pickers hide .heic files
// from selection entirely.
const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/heic,image/heif';
export const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB, matches API_FLOWS.md §2.1

function validateFile(file: File): string | null {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
    return 'Please upload a JPEG, PNG, or WebP image.';
  }
  if (file.size > MAX_FILE_BYTES) {
    return 'File is over 10 MB. Please upload a smaller image.';
  }
  return null;
}

/**
 * UploadDropzone — drag-and-drop + click-to-pick single-file input.
 *
 * Visual: white rounded card with dashed teal border. Centered teal circle
 * containing an up-arrow, then "Upload Photo" + the subject subtitle.
 * Selected file shows as a thumbnail preview with a remove button.
 */
export function UploadDropzone({ title, lines = [], file, onChange, disabled, error }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrl = file ? URL.createObjectURL(file) : null;

  const acceptFile = useCallback(
    async (f: File | undefined) => {
      if (!f) return;
      setConverting(true);
      let normalized: File;
      try {
        normalized = await normalizeImageFile(f);
      } catch {
        setConverting(false);
        setLocalError('Could not read that image. Please try a different file.');
        onChange(null);
        return;
      }
      setConverting(false);
      const err = validateFile(normalized);
      if (err) {
        setLocalError(err);
        onChange(null);
        return;
      }
      setLocalError(null);
      onChange(normalized);
    },
    [onChange],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      acceptFile(e.dataTransfer.files?.[0]);
    },
    [acceptFile, disabled],
  );

  const displayError = error ?? localError;

  return (
    <div className="flex flex-col w-full gap-1">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={title}
        aria-disabled={disabled}
        className={[
          'relative flex flex-col items-center justify-center min-h-[200px] rounded-[14px] border-2 border-dashed cursor-pointer transition-colors',
          'bg-white shadow-soft',
          disabled ? 'opacity-50 cursor-not-allowed' : '',
          dragOver
            ? 'border-teal bg-teal-dim'
            : displayError
              ? 'border-red-400 bg-red-50'
              : 'border-soft-border hover:border-teal',
        ].join(' ')}
      >
        {converting ? (
          <div className="text-center px-4 py-8">
            <p className="text-sm text-warm-brown/70">Converting photo…</p>
          </div>
        ) : previewUrl ? (
          <>
            <img
              src={previewUrl}
              alt={file?.name ?? 'preview'}
              className="max-h-40 max-w-full object-contain rounded-md"
            />
            <p className="mt-2 text-xs text-warm-brown/70">{title}</p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                onChange(null);
                setLocalError(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
              className="absolute top-3 right-3 rounded-full bg-warm-brown/80 text-white hover:bg-warm-brown"
              style={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 0 }}
              aria-label="Remove file"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </>
        ) : (
          <div className="text-center px-4 py-8 flex flex-col items-center gap-3">
            <span className="w-12 h-12 flex algin-center justify-center rounded-full bg-teal grid place-items-center shadow-sm">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
            </span>
            <div>
              <p className="text-sm font-semibold text-warm-brown">{title}</p>
              {lines.map((line, i) => (
                <p key={i} className="text-xs text-warm-brown/70 mt-0.5">{line}</p>
              ))}
            </div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          disabled={disabled}
          className="sr-only"
          onChange={(e) => acceptFile(e.target.files?.[0])}
        />
      </div>
      {displayError && <p className="text-xs text-red-600 mt-1">{displayError}</p>}
    </div>
  );
}
